import React, { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { PageHeader, StatusBadge, btn } from "@/components/kit";
import { toast } from "sonner";
import { Check, Loader2 } from "lucide-react";

export default function Billing() {
  const [org, setOrg] = useState(null);
  const [plans, setPlans] = useState([]);
  const [busy, setBusy] = useState("");

  useEffect(() => {
    api.get("/orgs/me").then((r) => setOrg(r.data)).catch(() => {});
    api.get("/billing/plans").then((r) => setPlans(r.data)).catch(() => {});
  }, []);

  const checkout = async (plan) => {
    setBusy(plan);
    try {
      const { data } = await api.post("/payments/checkout", { plan, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(apiError(e)); setBusy(""); }
  };

  if (!org) return <div className="animate-pulse text-muted-foreground overline">Loading…</div>;

  return (
    <div>
      <PageHeader title="Billing & plans" subtitle="Manage your OnboardHQ subscription." />

      <div className="bg-card border border-border p-6 mb-10 flex flex-wrap items-center justify-between gap-4" data-testid="current-plan-card">
        <div>
          <p className="overline text-muted-foreground">Current plan</p>
          <p className="text-2xl font-bold capitalize mt-1">{org.plan}</p>
        </div>
        <div className="text-right">
          <StatusBadge status={org.subscription_status} />
          {org.subscription_status === "trial" && org.trial_ends_at && (
            <p className="text-xs text-muted-foreground mt-2">Trial ends {new Date(org.trial_ends_at).toLocaleDateString()}</p>
          )}
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-px bg-border border border-border">
        {plans.map((p) => {
          const current = org.plan === p.key && org.subscription_status === "active";
          const featured = p.key === "growth";
          return (
            <div key={p.key} className={`p-8 flex flex-col ${featured ? "bg-primary text-primary-foreground" : "bg-card"}`} data-testid={`billing-plan-${p.key}`}>
              {featured && <span className="overline text-accent mb-3">Most popular</span>}
              <h3 className="text-xl font-bold">{p.name}</h3>
              <div className="mt-3 flex items-end gap-1"><span className="text-4xl font-black tracking-tighter">£{p.price}</span><span className={`mb-1 text-sm ${featured ? "text-primary-foreground/60" : "text-muted-foreground"}`}>/mo</span></div>
              <ul className="mt-6 space-y-2.5 flex-1">
                {p.features.map((f) => <li key={f} className="flex items-start gap-2 text-sm"><Check className="h-4 w-4 text-accent mt-0.5 shrink-0" /><span className={featured ? "text-primary-foreground/90" : "text-foreground/80"}>{f}</span></li>)}
              </ul>
              <button disabled={current || busy === p.key} onClick={() => checkout(p.key)} data-testid={`subscribe-${p.key}`}
                className={`mt-6 w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 font-semibold transition-colors disabled:opacity-60 ${featured ? "bg-accent text-accent-foreground hover:bg-primary-foreground hover:text-primary" : "bg-primary text-primary-foreground hover:bg-accent"}`}>
                {busy === p.key ? <Loader2 className="h-4 w-4 animate-spin" /> : current ? "Current plan" : "Subscribe"}
              </button>
            </div>
          );
        })}
      </div>
      <p className="mt-6 text-sm text-muted-foreground">Secure payments by Stripe. Test card: 4242 4242 4242 4242, any future expiry & CVC.</p>
    </div>
  );
}
